<script>
import { layoutMethods, walletFreezingMethods, erpFixMessages } from "@/state/helpers";
import Swal from "sweetalert2";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  page: {
    title: "Wallet Freezing",
  },
  components: {
    flatPickr,
  },
  data() {
    return {
      // Tab state
      activeTab: "user-wallet", // 'user-wallet' | 'logs'

      // Table data
      walletList: [],
      totalRecords: 0,
      loading: false,

      // Search & Filter
      searchField: "all",
      searchKeyword: "",
      filterStatus: "all",

      // Pagination
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],

      // Sorting
      sortBy: "created_at",
      sortDesc: true,

      // Search field options
      searchFieldOptions: [
        { value: "all", text: "Search All" },
        { value: "account_number", text: "Account Number" },
        { value: "account_name", text: "Account Name" },
        { value: "business_name", text: "Business Name" },
      ],

      // Table fields for b-table (User Wallet)
      walletFields: [
        { key: "account_number", label: "Account Number", sortable: true },
        { key: "account_name", label: "Account Name", sortable: true },
        { key: "business_name", label: "Business Name", sortable: true },
        { key: "wallet_status", label: "Wallet Status", sortable: true },
        // { key: "remaining_balance", label: "Remaining Balance", sortable: false },
        { key: "activity_logs", label: "Activity Logs", sortable: false },
      ],

      // Table fields for b-table (Logs)
      logsFields: [
        { key: "action", label: "Wallet Status", sortable: true },
        { key: "account_number", label: "Account Number", sortable: true },
        { key: "account_name", label: "Account Name", sortable: true },
        { key: "business_name", label: "Business Name", sortable: true },
        { key: "reason", label: "Reason", sortable: true },
        { key: "created_at", label: "Date & Time", sortable: true },
      ],

      // Filter status options
      filterStatusOptions: [
        { value: "all", text: "All" },
        { value: "enabled", text: "Enabled" },
        { value: "freeze", text: "Freeze" },
      ],

      // Logs tab data
      logsList: [],
      logsLoading: false,
      logsTotalRecords: 0,
      logsCurrentPage: 1,
      logsPerPage: 10,
      logsSearchField: "all",
      logsSearchKeyword: "",
      logsDateRange: "",
      logsFilterAction: "all",
      logsSortBy: "created_at",
      logsSortDesc: true,
      logsRangeOptions: {
        mode: "range",
        maxDate: "today",
      },

      // Status change modal
      statusModal: {
        show: false,
        wallet: null,
        newStatus: '',
        reason: '',
        submitting: false,
      },

      // View Log modal
      logModal: {
        show: false,
        wallet: null,
        logs: [],
        loading: false,
        totalRecords: 0,
        currentPage: 1,
        perPage: 10,
        dateRange: "",
        filterAction: "all",
        sortBy: "created_at",
        sortDesc: true,
      },
      logModalRangeOptions: {
        mode: "range",
        maxDate: "today",
      },

      // Table fields for View Log modal
      logModalFields: [
        { key: "action", label: "Wallet Status", sortable: true },
        { key: "reason", label: "Reason", sortable: true },
        { key: "created_at", label: "Date & Time", sortable: true },
        { key: "performed_by", label: "Modified By", sortable: true },
      ],

      // Reason options for freeze/enable
      freezeReasonOptions: [
        { value: '', text: 'Select Reason' },
        { value: 'Suspicious Transactions', text: 'Suspicious Transactions' },
        { value: 'Fraudulent Activity', text: 'Fraudulent Activity' },
        { value: 'Hacked Account', text: 'Hacked Account' },
        { value: 'Bugs/Issues', text: 'Bugs/Issues' },
        { value: 'Need Additional Requirements', text: 'Need Additional Requirements' },
      ],
      enableReasonOptions: [
        { value: '', text: 'Select Reason' },
        { value: 'Cleared by Management', text: 'Cleared by Management' },
        { value: 'Identified as Bugs/Issue', text: 'Identified as Bugs/Issue' },
        { value: 'No Hacking Identified', text: 'No Hacking Identified' },
        { value: 'Cleared from BSP Watchlist', text: 'Cleared from BSP Watchlist' },
      ],
    };
  },
  computed: {
    ...erpFixMessages,
    paginatedText() {
      return `${this.totalRecords} Record(s)`;
    },
  },
  async mounted() {
    this.changeTitle({ title: "Wallet Freezing" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "Kankolek Manager", to: { name: "" } },
        { text: "Wallet Freezing", to: { name: "" } },
      ],
    });
    document.addEventListener("keydown", this.handleEscKey);
    await this.fetchWallets();
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEscKey);
  },
  methods: {
    ...layoutMethods,
    ...walletFreezingMethods,

    async fetchWallets() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage,
          search_field: this.searchField,
          search_keyword: this.searchKeyword,
          filter_status: this.filterStatus,
          sort: this.sortBy,
          orderDesc: this.sortDesc,
        };
        const res = await this.actionWalletFreezing({
          action: "get-wallets",
          body: params,
        });
        if (res.status === "ok") {
          this.walletList = res.message?.data || [];
          this.totalRecords = res.message?.total || 0;
        }
      } catch (err) {
        console.error("Failed to fetch wallets:", err);
      } finally {
        this.loading = false;
      }
    },

    async fetchLogs() {
      this.logsLoading = true;
      try {
        const params = {
          page: this.logsCurrentPage,
          limit: this.logsPerPage,
          search_field: this.logsSearchField,
          search_keyword: this.logsSearchKeyword,
          date_range: this.logsDateRange,
          filter_action: this.logsFilterAction,
          sort: this.logsSortBy,
          orderDesc: this.logsSortDesc,
        };
        const res = await this.actionWalletFreezing({
          action: "get-logs",
          body: params,
        });
        if (res.status === "ok") {
          this.logsList = res.message?.data || [];
          this.logsTotalRecords = res.message?.total || 0;
        }
      } catch (err) {
        console.error("Failed to fetch logs:", err);
      } finally {
        this.logsLoading = false;
      }
    },

    async toggleWalletStatus(wallet) {
      const newStatus = wallet.wallet_status === "enabled" ? "freeze" : "enabled";
      this.statusModal = {
        show: true,
        wallet: wallet,
        newStatus: newStatus,
        reason: '',
        submitting: false,
        balanceLoading: true,
      };

      // Fetch balance on-demand for the modal
      this.actionWalletFreezing({
        action: "get-balance",
        body: { business_id: wallet.business_id },
      })
        .then((res) => {
          if (res.status === "ok") {
            this.$set(this.statusModal, 'balance', res.message?.balance || 0);
          }
        })
        .catch(() => {
          this.$set(this.statusModal, 'balance', 0);
        })
        .finally(() => {
          this.statusModal.balanceLoading = false;
        });
    },

    async confirmStatusChange() {
      if (!this.statusModal.reason) {
        Swal.fire({
          title: "Required",
          text: "Please select a reason.",
          icon: "warning",
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }

      this.statusModal.submitting = true;
      try {
        const res = await this.actionWalletFreezing({
          action: "update-wallet-status",
          body: {
            user_id: this.statusModal.wallet.user_id,
            business_id: this.statusModal.wallet.business_id,
            cognito_sub: this.statusModal.wallet.cognito_sub,
            status: this.statusModal.newStatus === "freeze" ? "freeze" : "enabled",
            reason: this.statusModal.reason,
          },
        });

        if (res.status === "ok") {
          this.$set(this.statusModal.wallet, 'wallet_status', this.statusModal.newStatus);
          let businessName = this.statusModal.wallet.business_name;
          let actionText = this.statusModal.newStatus === "freeze" ? "freeze" : "enabled";
          this.statusModal.show = false;
          Swal.fire({
            title: "Update Successful!",
            text: `${businessName} has been updated. The Kankolek wallet has been ${actionText}.`,
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#ffffff",
            customClass: {
              confirmButton: 'swal-btn-outlined',
            },
          });
        }
      } catch (err) {
        console.error("Failed to update wallet status:", err);
        Swal.fire({
          title: "Error",
          text: "Failed to update wallet status. Please try again.",
          icon: "error",
        });
      } finally {
        this.statusModal.submitting = false;
      }
    },

    cancelStatusChange() {
      this.statusModal.show = false;
      this.statusModal.wallet = null;
      this.statusModal.reason = '';
    },

    handleSearch() {
      this.currentPage = 1;
      this.fetchWallets();
    },

    handleRefresh() {
      this.searchKeyword = "";
      this.filterStatus = "all";
      this.currentPage = 1;
      this.fetchWallets();
    },

    async handleExport() {
      var self = this;
      this.currentPage = 1;
      await this.fetchWallets().then(function () {
        if (self.totalRecords == 0) {
          Swal.fire("Failed!", self.exportNullPrompt, "warning");
        } else {
          let params = {
            email: JSON.parse(localStorage.getItem("userData")).email,
            search_field: self.searchField,
            search_keyword: self.searchKeyword,
            filter_status: self.filterStatus,
            sort: self.sortBy,
            orderDesc: self.sortDesc,
          };

          self
            .actionWalletFreezing({ action: "export", body: params })
            .then(() =>
              Swal.fire(
                "Success!",
                `Your request for reports is pending. It will be sent to your email once it's ready.`,
                "success"
              )
            )
            .catch(() =>
              Swal.fire("Failed!", `Your request for reports has failed.`, "warning")
            );
        }
      });
    },

    onPageUpdate(reset = "") {
      if (reset === "reset") {
        this.currentPage = 1;
      }
      this.fetchWallets();
    },

    onDisplayChange() {
      this.currentPage = 1;
      this.fetchWallets();
    },

    handleSort(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.currentPage = 1;
      this.fetchWallets();
    },

    handleLogsSort(ctx) {
      this.logsSortBy = ctx.sortBy;
      this.logsSortDesc = ctx.sortDesc;
      this.logsCurrentPage = 1;
      this.fetchLogs();
    },

    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "logs" && this.logsList.length === 0) {
        this.fetchLogs();
      }
    },

    handleLogsSearch() {
      this.logsCurrentPage = 1;
      this.fetchLogs();
    },

    async handleLogsExport() {
      var self = this;
      this.logsCurrentPage = 1;
      await this.fetchLogs().then(function () {
        if (self.logsTotalRecords == 0) {
          Swal.fire("Failed!", self.exportNullPrompt, "warning");
        } else {
          let params = {
            email: JSON.parse(localStorage.getItem("userData")).email,
            date_range: self.logsDateRange,
            filter_action: self.logsFilterAction,
            sort: self.logsSortBy,
            orderDesc: self.logsSortDesc,
          };

          self
            .actionWalletFreezing({ action: "export-logs", body: params })
            .then(() =>
              Swal.fire(
                "Success!",
                `Your request for reports is pending. It will be sent to your email once it's ready.`,
                "success"
              )
            )
            .catch(() =>
              Swal.fire("Failed!", `Your request for reports has failed.`, "warning")
            );
        }
      });
    },

    onLogsPageUpdate() {
      this.fetchLogs();
    },

    onLogsDisplayChange() {
      this.logsCurrentPage = 1;
      this.fetchLogs();
    },

    viewLog(wallet) {
      this.logModal.show = true;
      this.logModal.wallet = wallet;
      this.logModal.logs = [];
      this.logModal.dateRange = "";
      this.logModal.filterAction = "all";
      this.logModal.currentPage = 1;
      this.logModal.sortBy = "created_at";
      this.logModal.sortDesc = true;
      this.fetchUserLogs();
    },

    fetchUserLogs() {
      this.logModal.loading = true;

      return this.actionWalletFreezing({
        action: "get-logs",
        body: {
          user_id: this.logModal.wallet?.user_id,
          page: this.logModal.currentPage,
          limit: this.logModal.perPage,
          date_range: this.logModal.dateRange,
          filter_action: this.logModal.filterAction,
          sort: this.logModal.sortBy,
          orderDesc: this.logModal.sortDesc,
        },
      })
        .then((res) => {
          if (res.status === "ok") {
            this.logModal.logs = res.message?.data || [];
            this.logModal.totalRecords = res.message?.total || 0;
          }
        })
        .catch((err) => {
          console.error("Failed to fetch user logs:", err);
        })
        .finally(() => {
          this.logModal.loading = false;
        });
    },

    handleLogModalSearch() {
      this.logModal.currentPage = 1;
      this.fetchUserLogs();
    },

    async handleLogModalExport() {
      var self = this;
      this.logModal.currentPage = 1;
      await this.fetchUserLogs().then(function () {
        if (self.logModal.totalRecords == 0) {
          Swal.fire("Failed!", self.exportNullPrompt, "warning");
        } else {
          let params = {
            email: JSON.parse(localStorage.getItem("userData")).email,
            user_id: self.logModal.wallet?.user_id,
            date_range: self.logModal.dateRange,
            filter_action: self.logModal.filterAction,
          };

          self
            .actionWalletFreezing({ action: "export-logs", body: params })
            .then(() =>
              Swal.fire(
                "Success!",
                `Your request for reports is pending. It will be sent to your email once it's ready.`,
                "success"
              )
            )
            .catch(() =>
              Swal.fire("Failed!", `Your request for reports has failed.`, "warning")
            );
        }
      });
    },

    onLogModalPageUpdate() {
      this.fetchUserLogs();
    },

    handleLogModalSort(ctx) {
      this.logModal.sortBy = ctx.sortBy;
      this.logModal.sortDesc = ctx.sortDesc;
      this.logModal.currentPage = 1;
      this.fetchUserLogs();
    },

    onLogModalDisplayChange() {
      this.logModal.currentPage = 1;
      this.fetchUserLogs();
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return "PHP 0.00";
      return `PHP ${Number(amount).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    handleEscKey(e) {
      if (e.key === "Escape" && this.logModal.show) {
        this.logModal.show = false;
      }
    },
  },
};
</script>

<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-body">
          <!-- Tabs -->
          <div class="wallet-tabs mb-4">
            <div class="tab-nav">
              <button
                class="tab-btn"
                :class="{ active: activeTab === 'user-wallet' }"
                @click="switchTab('user-wallet')"
              >
                User Wallet
              </button>
              <button
                class="tab-btn"
                :class="{ active: activeTab === 'logs' }"
                @click="switchTab('logs')"
              >
                Log
              </button>
            </div>
          </div>

          <!-- User Wallet Tab -->
          <div v-if="activeTab === 'user-wallet'">
            <!-- Record count and Refresh -->
            <div class="d-flex align-items-center justify-content-between mb-4">
              <h5 class="mb-0 fw-bold">{{ paginatedText }}</h5>
              <button class="btn btn-secondary" @click="handleRefresh">
                <feather type="refresh-ccw" size="16" class="icon nav-icon"></feather>&nbsp;&nbsp;Refresh
              </button>
            </div>

            <!-- Search & Filter -->
            <div class="d-flex align-items-start gap-3 mb-4">
              <!-- Search field -->
              <div class="search-group">
                <div class="input-group">
                  <select
                    class="form-select search-field-select"
                    v-model="searchField"
                  >
                    <option
                      v-for="opt in searchFieldOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.text }}
                    </option>
                  </select>
                  <input
                    type="text"
                    class="form-control search-input"
                    v-model="searchKeyword"
                    placeholder=""
                    @keyup.enter="handleSearch"
                  />
                </div>
                <small class="text-end d-block mt-1">Search Keyword</small>
              </div>

              <!-- Filter by status -->
              <div class="filter-group">
                <select
                  class="form-select filter-select"
                  v-model="filterStatus"
                >
                  <option
                    v-for="opt in filterStatusOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.text }}
                  </option>
                </select>
                <small class="text-end d-block mt-1">Filter by Wallet Status</small>
              </div>

              <!-- Search & Export buttons -->
              <div class="d-flex gap-2">
                <button class="btn btn-search" @click="handleSearch">
                  <i class="bx bx-search"></i> Search
                </button>
                <button class="btn btn-export" @click="handleExport">
                  <i class="bx bx-upload"></i> Export
                </button>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive">
              <div class="table align-middle table-nowrap">
                <b-table
                  :items="walletList"
                  :fields="walletFields"
                  responsive="sm"
                  :sort-by.sync="sortBy"
                  :sort-desc.sync="sortDesc"
                  :no-local-sorting="true"
                  class="table-check"
                  @sort-changed="handleSort"
                >
                  <template #cell(wallet_status)="data">
                    <div class="wallet-status-toggle">
                      <label class="switch-toggle">
                        <input
                          type="checkbox"
                          :checked="data.item.wallet_status === 'enabled'"
                          @change="toggleWalletStatus(data.item)"
                        />
                        <span
                          class="slider"
                          :class="{
                            'slider-enabled': data.item.wallet_status === 'enabled',
                            'slider-freeze': data.item.wallet_status === 'freeze',
                          }"
                        ></span>
                      </label>
                      <span
                        class="status-label"
                        :class="{
                          'text-success-custom': data.item.wallet_status === 'enabled',
                          'text-danger-custom': data.item.wallet_status === 'freeze',
                        }"
                      >
                        {{ data.item.wallet_status === 'enabled' ? 'Enabled' : 'Freeze' }}
                      </span>
                    </div>
                  </template>

                  <!-- <template #cell(remaining_balance)="data">
                    {{ formatCurrency(data.item.remaining_balance) }}
                  </template> -->

                  <template #cell(activity_logs)="data">
                    <a
                      href="javascript:void(0);"
                      class="view-log-link"
                      @click="viewLog(data.item)"
                    >
                      View Log
                    </a>
                  </template>
                </b-table>
              </div>
            </div>

            <div class="row g-0 text-center" v-if="walletList.length === 0 && !loading">
              <span>No data to display.</span>
            </div>

            <!-- Pagination -->
            <div class="row g-0 text-center text-sm-start" v-if="totalRecords > perPage">
              <div class="col-sm-12">
                <div class="row">
                  <div class="col">
                    <div class="form-group row pull-left">
                      <label class="col-sm-6 col-form-label">Display:</label>
                      <div class="col-sm-6">
                        <b-form-group label-for="display-record">
                          <b-form-select
                            class="form-select shadow-none w-fit"
                            id="display-record"
                            v-model="perPage"
                            :options="pageOptions"
                            @change="onDisplayChange()"
                          ></b-form-select>
                        </b-form-group>
                      </div>
                    </div>
                    <div
                      class="dataTables_paginate paging_simple_numbers float-end"
                      v-if="totalRecords > perPage"
                    >
                      <b-pagination
                        class="pagination-rounded"
                        v-model="currentPage"
                        @input="onPageUpdate()"
                        :total-rows="totalRecords"
                        :per-page="perPage"
                      ></b-pagination>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Logs Tab -->
          <div v-if="activeTab === 'logs'">
            <!-- Filters -->
            <div class="d-flex align-items-start gap-3 mb-4">
              <div class="search-group">
                <div class="input-group">
                  <select
                    class="form-select search-field-select"
                    v-model="logsSearchField"
                  >
                    <option
                      v-for="opt in searchFieldOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.text }}
                    </option>
                  </select>
                  <input
                    type="text"
                    class="form-control search-input"
                    v-model="logsSearchKeyword"
                    placeholder=""
                    @keyup.enter="handleLogsSearch"
                  />
                </div>
                <small class="text-end d-block mt-1">Search Keyword</small>
              </div>

              <div class="filter-group">
                <flat-pickr
                  v-model="logsDateRange"
                  :config="logsRangeOptions"
                  placeholder="Select date range"
                  class="form-control"
                ></flat-pickr>
                <small class="text-end d-block mt-1">Filter by date</small>
              </div>

              <div class="filter-group">
                <select
                  class="form-select filter-select"
                  v-model="logsFilterAction"
                >
                  <option value="all">All</option>
                  <option value="freeze">Freeze</option>
                  <option value="enabled">Enabled</option>
                </select>
                <small class="text-end d-block mt-1">Filter by Wallet Status</small>
              </div>

              <div class="d-flex gap-2">
                <button class="btn btn-search" @click="handleLogsSearch">
                  <i class="bx bx-search"></i> Search
                </button>
              </div>
            </div>

            <!-- Logs Table -->
            <div class="table-responsive">
              <div class="table align-middle table-nowrap">
                <b-table
                  :items="logsList"
                  :fields="logsFields"
                  responsive="sm"
                  :sort-by.sync="logsSortBy"
                  :sort-desc.sync="logsSortDesc"
                  :no-local-sorting="true"
                  class="table-check"
                  @sort-changed="handleLogsSort"
                >
                  <template #cell(action)="data">
                    {{ data.item.action === 'freeze' ? 'Freezed' : 'Enabled' }}
                  </template>
                </b-table>
              </div>
            </div>

            <div class="row g-0 text-center" v-if="logsList.length === 0 && !logsLoading">
              <span>No data to display.</span>
            </div>

            <!-- Logs Pagination -->
            <div class="row g-0 text-center text-sm-start" v-if="logsTotalRecords > logsPerPage">
              <div class="col-sm-12">
                <div class="row">
                  <div class="col">
                    <div class="form-group row pull-left">
                      <label class="col-sm-6 col-form-label">Display:</label>
                      <div class="col-sm-6">
                        <b-form-group label-for="display-logs">
                          <b-form-select
                            class="form-select shadow-none w-fit"
                            id="display-logs"
                            v-model="logsPerPage"
                            :options="pageOptions"
                            @change="onLogsDisplayChange()"
                          ></b-form-select>
                        </b-form-group>
                      </div>
                    </div>
                    <div
                      class="dataTables_paginate paging_simple_numbers float-end"
                      v-if="logsTotalRecords > logsPerPage"
                    >
                      <b-pagination
                        class="pagination-rounded"
                        v-model="logsCurrentPage"
                        @input="onLogsPageUpdate()"
                        :total-rows="logsTotalRecords"
                        :per-page="logsPerPage"
                      ></b-pagination>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Freeze/Enable Status Modal -->
    <b-modal
      v-model="statusModal.show"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      content-class="status-modal-wrapper"
      body-class="p-0"
      @hidden="cancelStatusChange"
    >
      <div class="status-modal-card" v-if="statusModal.wallet">
        <!-- Modal Header with X close -->
        <div class="status-modal-header-row">
          <h5 class="mb-0 fw-bold">
            Freeze Kankolek Wallet
          </h5>
          <button type="button" class="btn-close-modal" @click="cancelStatusChange">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <!-- Subtitle -->
        <p class="status-modal-subtitle">
          {{ statusModal.newStatus === 'freeze'
            ? 'Before you freeze the Kankolek Wallet. Please let us know the reason.'
            : 'Before you enable the Kankolek Wallet. Please let us know the reason.'
          }}
        </p>

        <!-- Account Details (read-only info card) -->
        <div class="account-info-card mb-3">
          <div class="info-row">
            <span class="info-label">Business Name:</span>
            <span class="info-value">{{ statusModal.wallet.business_name }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Account Number:</span>
            <span class="info-value">{{ statusModal.wallet.account_number }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Account Name:</span>
            <span class="info-value">{{ statusModal.wallet.account_name }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Remaining Balance:</span>
            <span class="info-value" v-if="!statusModal.balanceLoading">
              {{ formatCurrency(statusModal.balance) }}
            </span>
            <span class="info-value text-muted" v-else>Loading...</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value" :class="statusModal.wallet.wallet_status === 'enabled' ? 'status-active' : 'status-freeze'">
              {{ statusModal.wallet.wallet_status === 'enabled' ? 'Active' : 'Freeze' }}
            </span>
          </div>
        </div>

        <!-- Reason Dropdown -->
        <div class="reason-field mb-4">
          <label class="form-label fw-bold">Reason<span class="text-danger">*</span></label>
          <b-form-select
            v-model="statusModal.reason"
            :options="statusModal.newStatus === 'freeze' ? freezeReasonOptions : enableReasonOptions"
            class="form-select"
          ></b-form-select>
        </div>

        <!-- Action Buttons -->
        <div class="status-modal-actions">
          <button
            class="btn btn-confirm-action w-100 mb-2"
            :class="statusModal.newStatus === 'freeze' ? 'btn-freeze' : 'btn-enable'"
            @click="confirmStatusChange"
            :disabled="statusModal.submitting || !statusModal.reason"
          >
            <span v-if="statusModal.submitting">
              <i class="bx bx-loader-alt bx-spin"></i> Processing...
            </span>
            <span v-else>
              {{ statusModal.newStatus === 'freeze' ? 'Confirm Freeze' : 'Confirm Enable' }}
            </span>
          </button>
          <button
            class="btn btn-cancel-modal w-100"
            @click="cancelStatusChange"
            :disabled="statusModal.submitting"
          >
            Cancel
          </button>
        </div>
      </div>
    </b-modal>

    <!-- View Log Modal (per user) -->
    <b-modal
      v-model="logModal.show"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      size="lg"
      content-class="log-modal-wrapper"
      body-class="p-0"
      @hidden="logModal.show = false"
    >
      <div class="log-modal-card">
        <!-- Header -->
        <div class="log-modal-header">
          <h5 class="mb-0 fw-bold">Activity Logs: Wallet Freezing</h5>
        </div>

        <div class="log-modal-body">
          <!-- Search & Filter -->
          <div class="d-flex align-items-start gap-3 mb-3">
            <div class="filter-group">
              <flat-pickr
                v-model="logModal.dateRange"
                :config="logModalRangeOptions"
                placeholder="Select date range"
                class="form-control"
              ></flat-pickr>
              <small class="text-end d-block mt-1">Filter by date</small>
            </div>

            <div class="filter-group">
              <select class="form-select filter-select" v-model="logModal.filterAction">
                <option value="all">All</option>
                <option value="enabled">Enabled</option>
                <option value="freeze">Freeze</option>
              </select>
              <small class="text-end d-block mt-1">Filter by Wallet Status</small>
            </div>

            <div class="d-flex gap-2">
              <button class="btn btn-search" @click="handleLogModalSearch">
                <i class="bx bx-search"></i> Search
              </button>
              <button class="btn btn-export" @click="handleLogModalExport">
                <i class="bx bx-upload"></i> Export
              </button>
            </div>
          </div>

          <!-- Table -->
          <div v-if="logModal.logs.length === 0 && !logModal.loading" class="text-center py-4 text-muted">
            No activity logs found for this account.
          </div>

          <div v-else class="table-responsive">
            <div class="table align-middle table-nowrap">
              <b-table
                :items="logModal.logs"
                :fields="logModalFields"
                responsive="sm"
                :sort-by.sync="logModal.sortBy"
                :sort-desc.sync="logModal.sortDesc"
                :no-local-sorting="true"
                class="table-check"
                @sort-changed="handleLogModalSort"
              >
                <template #cell(action)="data">
                  {{ data.item.action === 'freeze' ? 'Freeze' : 'Enabled' }}
                </template>
              </b-table>
            </div>
          </div>

          <!-- Pagination -->
          <div class="d-flex align-items-center justify-content-between mt-3" v-if="logModal.totalRecords > logModal.perPage">
            <div class="form-group row pull-left mb-0">
              <label class="col-auto col-form-label">Display</label>
              <div class="col-auto">
                <b-form-select
                  class="form-select shadow-none w-fit"
                  v-model="logModal.perPage"
                  :options="pageOptions"
                  @change="onLogModalDisplayChange()"
                ></b-form-select>
              </div>
            </div>
            <b-pagination
              v-if="logModal.totalRecords > logModal.perPage"
              class="pagination-rounded mb-0"
              v-model="logModal.currentPage"
              @input="onLogModalPageUpdate()"
              :total-rows="logModal.totalRecords"
              :per-page="logModal.perPage"
            ></b-pagination>
          </div>

          <!-- Close button -->
          <div class="d-flex justify-content-end mt-4">
            <button class="btn btn-cancel-modal" @click="logModal.show = false">
              Close
            </button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
/* Tabs */
.wallet-tabs {
  border-bottom: 2px solid #edecec;
}

.tab-nav {
  display: flex;
  gap: 0;
}

.tab-btn {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #000;
  cursor: pointer;
  margin-bottom: -2px;
  transition: all 0.2s;
}

.tab-btn.active {
  color: #e83092;
  border-bottom-color: #e83092;
}

.tab-btn:hover:not(.active) {
  color: #7f4697;
}

/* Buttons */
.btn-search {
  background-color: #e83092;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-search:hover {
  background-color: #d12882;
  color: #fff;
}

.btn-export {
  background-color: #e83092;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-export:hover {
  background-color: #d12882;
  color: #fff;
}

/* Search & Filter */
.search-group .input-group {
  width: 285px;
}

.search-field-select {
  max-width: 113px;
  font-size: 14px;
  border-color: #e0e0e0;
  border-radius: 4px 0 0 4px;
}

.search-input {
  font-size: 14px;
  border-color: #e0e0e0;
  border-radius: 0 4px 4px 0;
}

.filter-select {
  width: 172px;
  font-size: 14px;
  border-color: #e0e0e0;
  border-radius: 4px;
}

/* Table */
.table-wallet {
  font-size: 12px;
}

.table-wallet thead th {
  font-weight: 700;
  font-size: 12px;
  color: #000;
  border-bottom: 1px solid #e0e0e0;
  padding: 10px 20px;
  white-space: nowrap;
}

.table-wallet tbody td {
  font-weight: 500;
  font-size: 12px;
  color: #202020;
  padding: 10px 20px;
  vertical-align: middle;
  border-bottom: 1px solid #e0e0e0;
}

/* Toggle Switch */
.wallet-status-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
}

.switch-toggle {
  position: relative;
  display: inline-block;
  width: 29px;
  height: 16px;
  margin-bottom: 0;
}

.switch-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 39px;
  transition: 0.3s;
}

.slider::before {
  position: absolute;
  content: "";
  height: 13px;
  width: 13px;
  border-radius: 50%;
  background-color: white;
  top: 1.5px;
  transition: 0.3s;
}

.slider-enabled {
  background-color: #7f4697;
}

.slider-enabled::before {
  left: 14px;
}

.slider-freeze {
  background-color: #831118;
  border: 0.667px solid #831118;
}

.slider-freeze::before {
  left: 1.5px;
}

.status-label {
  font-size: 12px;
  font-weight: 500;
}

.text-success-custom {
  color: #508f30;
}

.text-danger-custom {
  color: #831118;
}

/* View Log Link */
.view-log-link {
  color: #e83092;
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
}

.view-log-link:hover {
  color: #d12882;
}

/* Log Action Badges */
.badge-freeze {
  background-color: #fde8e8;
  color: #B91C1C;
  font-weight: 600;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
}

.badge-enabled {
  background-color: #e8f5e2;
  color: #508f30;
  font-weight: 600;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
}

/* Pagination */
.display-count {
  font-size: 12px;
}

/* Log Modal */
.log-modal-wrapper {
  border: none;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}

.log-modal-card {
  border-radius: 8px;
  overflow: hidden;
}

.log-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e6eaf1;
}

.log-modal-header h5 {
  font-size: 18px;
  color: #202020;
}

.log-modal-body {
  padding: 20px 24px;
}

.table-log {
  font-size: 12px;
}

.table-log thead th {
  font-weight: 700;
  font-size: 12px;
  color: #000;
  border-bottom: 1px solid #e0e0e0;
  padding: 10px 16px;
  white-space: nowrap;
}

.table-log tbody td {
  font-weight: 500;
  font-size: 12px;
  color: #202020;
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #e6eaf1;
}

.log-modal-body .filter-group {
  flex: 1;
}

.log-modal-body .filter-select {
  width: 100%;
}

/* Status Modal */
.status-modal-wrapper {
  border: none;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}

.status-modal-card {
  padding: 24px;
}

.status-modal-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.status-modal-header-row h5 {
  font-size: 20px;
  color: #202020;
}

.btn-close-modal {
  background: none;
  border: none;
  font-size: 24px;
  color: #485056;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.btn-close-modal:hover {
  color: #202020;
}

.status-modal-subtitle {
  font-size: 14px;
  color: #485056;
  margin-bottom: 20px;
  line-height: 1.5;
}

.account-info-card {
  background-color: #f9f9f9;
  border: 1px solid #e6eaf1;
  border-radius: 8px;
  padding: 12px 20px;
}

.account-info-card .info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e6eaf1;
}

.account-info-card .info-row:last-child {
  border-bottom: none;
}

.account-info-card .info-label {
  font-size: 13px;
  font-weight: 400;
  color: #6c757d;
}

.account-info-card .info-value {
  font-size: 13px;
  font-weight: 600;
  color: #202020;
}

.account-info-card .info-value.status-active {
  color: #16a34a;
  font-weight: 700;
}

.account-info-card .info-value.status-freeze {
  color: #B91C1C;
  font-weight: 700;
}

.status-modal-actions {
  margin-top: 8px;
}

.btn-cancel-modal {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  color: #485056;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
}

.btn-cancel-modal:hover {
  background-color: #f5f5f5;
  border-color: #ccc;
  color: #485056;
}

.btn-freeze {
  background-color: #B91C1C;
  color: #fff;
  border: none;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
}

.btn-freeze:hover {
  background-color: #991b1b;
  color: #fff;
}

.btn-freeze:disabled {
  background-color: #B91C1C;
  opacity: 0.6;
  color: #fff;
}

.btn-enable {
  background-color: #e83092;
  color: #fff;
  border: none;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
}

.btn-enable:hover {
  background-color: #d12882;
  color: #fff;
}

.btn-enable:disabled {
  background-color: #e83092;
  opacity: 0.6;
  color: #fff;
}
</style>

<style>
/* SweetAlert custom button - unscoped so it applies to Swal popup */
.swal-btn-outlined {
  background-color: #fff !important;
  color: #485056 !important;
  border: 1px solid #d1d5db !important;
  font-weight: 500 !important;
  padding: 8px 32px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
}

.swal-btn-outlined:hover {
  background-color: #f5f5f5 !important;
}

.swal-btn-outlined:focus {
  box-shadow: none !important;
}
</style>
